import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './app/App';
import { ModalProvider } from './app/contexts/ModalContext';
import { ThemeProvider } from './app/contexts/ThemeContext';
import { registerServiceWorker } from './registerServiceWorker';
import { queryClient } from './shared/api/queryClient';
import { AuthProvider } from './shared/auth/AuthContext';
import { AutoSyncProvider } from './shared/contexts/AutoSyncContext';

// Import global styles
import './styles/tailwind.css';
import '@shared/styles/tokens.css';
import '@shared/styles/tables.css';
import './styles/mobile.css';

/**
 * Top-level error boundary to catch unhandled errors (e.g., from Suspense, providers).
 * Without this, an unhandled error crashes the entire React tree, leaving a blank page.
 */
class AppErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Top-level error boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          padding: '20px',
          textAlign: 'center',
        }}>
          <p style={{ fontSize: '18px', color: '#666', marginBottom: '16px' }}>
            Something went wrong. Please refresh the page.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 24px',
              fontSize: '16px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function mountReactShell() {
  const rootEl = document.getElementById('root');
  if (!rootEl) {
    return;
  }
  if ((rootEl as Element & { _reactRoot?: unknown })._reactRoot) return; // prevent double mount

  const root = createRoot(rootEl);
  (rootEl as Element & { _reactRoot?: unknown })._reactRoot = root;

  root.render(
    <AppErrorBoundary>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <AutoSyncProvider>
              <ThemeProvider>
                <ModalProvider>
                  <App />
                </ModalProvider>
              </ThemeProvider>
            </AutoSyncProvider>
          </AuthProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </BrowserRouter>
    </AppErrorBoundary>
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountReactShell);
} else {
  mountReactShell();
}

registerServiceWorker();
